/**
 * IndexedDB Helper for Offline Storage
 * Provides wrapper functions for storing photos, verification sessions, and voice notes locally
 */

const DB_NAME = 'BuildZimOfflinePhotos';
const DB_VERSION = 2;
const STORE_NAME = 'photos';
const SESSIONS_STORE = 'verification_sessions';
const VOICE_NOTES_STORE = 'voice_notes';

class IndexedDBHelper {
  constructor() {
    this.db = null;
  }

  /**
   * Initialize and open the IndexedDB database
   */
  async openDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // v1: Photos store
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const photoStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          photoStore.createIndex('timestamp', 'timestamp', { unique: false });
          photoStore.createIndex('uploaded', 'uploaded', { unique: false });
          photoStore.createIndex('verificationId', 'verificationId', { unique: false });
          photoStore.createIndex('projectId', 'projectId', { unique: false });
        }

        // v2: Add sessionId index to photos store
        if (event.oldVersion < 2) {
          const photoStore = event.target.transaction.objectStore(STORE_NAME);
          if (!photoStore.indexNames.contains('sessionId')) {
            photoStore.createIndex('sessionId', 'sessionId', { unique: false });
          }
        }

        // v2: Verification sessions store
        if (!db.objectStoreNames.contains(SESSIONS_STORE)) {
          const sessionStore = db.createObjectStore(SESSIONS_STORE, { keyPath: 'sessionId' });
          sessionStore.createIndex('verificationId', 'verificationId', { unique: false });
          sessionStore.createIndex('status', 'status', { unique: false });
          sessionStore.createIndex('startedAt', 'startedAt', { unique: false });
        }

        // v2: Voice notes store
        if (!db.objectStoreNames.contains(VOICE_NOTES_STORE)) {
          const voiceStore = db.createObjectStore(VOICE_NOTES_STORE, { keyPath: 'id' });
          voiceStore.createIndex('sessionId', 'sessionId', { unique: false });
          voiceStore.createIndex('verificationId', 'verificationId', { unique: false });
          voiceStore.createIndex('capturedAt', 'capturedAt', { unique: false });
        }
      };
    });
  }

  /**
   * Ensure database is open before operations
   */
  async ensureDB() {
    if (!this.db) {
      await this.openDatabase();
    }
    return this.db;
  }

  /**
   * Store a photo in IndexedDB
   * @param {Object} photoData - Photo data including base64, metadata, GPS
   */
  async storePhoto(photoData) {
    await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const request = store.add(photoData);

      request.onsuccess = () => {
        resolve(photoData.id);
      };

      request.onerror = () => {
        reject(new Error('Failed to store photo in IndexedDB'));
      };
    });
  }

  /**
   * Get a single photo by ID
   * @param {string} photoId - Photo ID
   */
  async getPhoto(photoId) {
    await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(photoId);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(new Error('Failed to retrieve photo from IndexedDB'));
      };
    });
  }

  /**
   * Get all photos (with optional filtering)
   * @param {Object} filters - Optional filters { uploaded: boolean, verificationId: string }
   */
  async getAllPhotos(filters = {}) {
    await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        let photos = request.result;

        // Apply filters
        if (filters.uploaded !== undefined) {
          photos = photos.filter(p => p.uploaded === filters.uploaded);
        }
        if (filters.verificationId) {
          photos = photos.filter(p => p.verificationId === filters.verificationId);
        }
        if (filters.projectId) {
          photos = photos.filter(p => p.projectId === filters.projectId);
        }

        // Sort by timestamp (most recent first)
        photos.sort((a, b) => b.timestamp - a.timestamp);

        resolve(photos);
      };

      request.onerror = () => {
        reject(new Error('Failed to retrieve photos from IndexedDB'));
      };
    });
  }

  /**
   * Update a photo (mark as uploaded, add server URL, etc.)
   * @param {string} photoId - Photo ID
   * @param {Object} updates - Updates to apply
   */
  async updatePhoto(photoId, updates) {
    await this.ensureDB();

    return new Promise(async (resolve, reject) => {
      try {
        // Get existing photo
        const photo = await this.getPhoto(photoId);
        if (!photo) {
          reject(new Error('Photo not found'));
          return;
        }

        // Apply updates
        const updatedPhoto = { ...photo, ...updates };

        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(updatedPhoto);

        request.onsuccess = () => {
          resolve(updatedPhoto);
        };

        request.onerror = () => {
          reject(new Error('Failed to update photo in IndexedDB'));
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Delete a photo from IndexedDB
   * @param {string} photoId - Photo ID
   */
  async deletePhoto(photoId) {
    await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(photoId);

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        reject(new Error('Failed to delete photo from IndexedDB'));
      };
    });
  }

  /**
   * Get pending upload count (photos not yet uploaded)
   */
  async getPendingUploadCount() {
    const photos = await this.getAllPhotos({ uploaded: false });
    return photos.length;
  }

  /**
   * Get storage usage estimate
   */
  async getStorageEstimate() {
    if (navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage,
        quota: estimate.quota,
        usageInMB: (estimate.usage / (1024 * 1024)).toFixed(2),
        quotaInMB: (estimate.quota / (1024 * 1024)).toFixed(2),
        percentUsed: ((estimate.usage / estimate.quota) * 100).toFixed(2)
      };
    }
    return null;
  }

  /**
   * Clear all photos (use with caution)
   */
  async clearAllPhotos() {
    await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = () => {
        reject(new Error('Failed to clear photos from IndexedDB'));
      };
    });
  }

  /**
   * Delete old uploaded photos (cleanup)
   * @param {number} daysOld - Delete photos uploaded more than X days ago
   */
  async cleanupOldPhotos(daysOld = 30) {
    const photos = await this.getAllPhotos({ uploaded: true });
    const cutoffTime = Date.now() - (daysOld * 24 * 60 * 60 * 1000);

    const deletePromises = photos
      .filter(p => p.uploadedAt && p.uploadedAt < cutoffTime)
      .map(p => this.deletePhoto(p.id));

    const results = await Promise.allSettled(deletePromises);
    const deletedCount = results.filter(r => r.status === 'fulfilled').length;

    return {
      deletedCount,
      totalScanned: photos.length
    };
  }

  // ─── Generic store operations (for sessions and voice notes) ───

  /**
   * Put a record into any object store
   */
  async putRecord(storeName, record) {
    await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([storeName], 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.put(record);
      request.onsuccess = () => resolve(record);
      request.onerror = () => reject(new Error(`Failed to put record in ${storeName}`));
    });
  }

  /**
   * Get a record by key from any object store
   */
  async getRecord(storeName, key) {
    await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([storeName], 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(new Error(`Failed to get record from ${storeName}`));
    });
  }

  /**
   * Get all records from a store, optionally filtered by an index value
   */
  async getAllFromStore(storeName, indexName, indexValue) {
    await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([storeName], 'readonly');
      const store = tx.objectStore(storeName);
      let request;
      if (indexName && indexValue !== undefined) {
        const index = store.index(indexName);
        request = index.getAll(indexValue);
      } else {
        request = store.getAll();
      }
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error(`Failed to get records from ${storeName}`));
    });
  }

  /**
   * Delete a record by key from any object store
   */
  async deleteRecord(storeName, key) {
    await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction([storeName], 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.delete(key);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(new Error(`Failed to delete record from ${storeName}`));
    });
  }

  // ─── Verification Session helpers ───

  async putSession(session) {
    return this.putRecord(SESSIONS_STORE, session);
  }

  async getSession(sessionId) {
    return this.getRecord(SESSIONS_STORE, sessionId);
  }

  async getSessionByVerification(verificationId) {
    const sessions = await this.getAllFromStore(SESSIONS_STORE, 'verificationId', verificationId);
    // Return the most recent non-synced session, or null
    return sessions
      .filter(s => s.status !== 'synced')
      .sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0))[0] || null;
  }

  async getAllSessions(status) {
    if (status) {
      return this.getAllFromStore(SESSIONS_STORE, 'status', status);
    }
    return this.getAllFromStore(SESSIONS_STORE);
  }

  async deleteSession(sessionId) {
    return this.deleteRecord(SESSIONS_STORE, sessionId);
  }

  // ─── Voice Note helpers ───

  async putVoiceNote(voiceNote) {
    return this.putRecord(VOICE_NOTES_STORE, voiceNote);
  }

  async getVoiceNote(id) {
    return this.getRecord(VOICE_NOTES_STORE, id);
  }

  async getVoiceNotesBySession(sessionId) {
    return this.getAllFromStore(VOICE_NOTES_STORE, 'sessionId', sessionId);
  }

  async deleteVoiceNote(id) {
    return this.deleteRecord(VOICE_NOTES_STORE, id);
  }

  /**
   * Get photos by session ID
   */
  async getPhotosBySession(sessionId) {
    const allPhotos = await this.getAllPhotos();
    return allPhotos.filter(p => p.sessionId === sessionId);
  }
}

// Export singleton instance and store name constants
export const STORE_NAMES = { PHOTOS: STORE_NAME, SESSIONS: SESSIONS_STORE, VOICE_NOTES: VOICE_NOTES_STORE };
const indexedDBHelper = new IndexedDBHelper();
export default indexedDBHelper;
